import { Form, useArrayField, useFormApi } from "informed";
import * as React from "react";
import { DeviceType, NotificationSubscription, Role, User } from "webportalclient";
import { useCurrentStudyConfigurationContext } from "../Core/CurrentStudyConfigurationContext";
import { Checkbox } from "../forms/informedv2/Checkbox";
import { MultiSelect } from "../forms/informedv2/MultiSelect";
import { TextBox } from "../forms/informedv2/TextBox";
import { useWebPortalApiClient } from "../misc/api/WebPortalTokenClient";
import Alert, { AlertCategoryEnum } from "../misc/components/Alert";
import AsyncActionButton from "../misc/components/AsyncActionButton";
import { NotificationPlugin } from "./NotificationTypes";

export interface NotificationConfigProps {
    selectedTemplate: NotificationPlugin;
    existingSubscription?: undefined | NotificationSubscription;
    isEdit: boolean;
    onClose: () => void;
}

interface NotificationConfigFormState {
    users: User[];
    roles: Role[];
    notifyAuthor: boolean;
    notifySite: boolean;
    configurableParameters: any[];
}

interface CreateNotificationConfigParametersProps {
    selectedTemplate: NotificationPlugin;
}

export const CreateNotificationConfigParameters = ({ selectedTemplate }: CreateNotificationConfigParametersProps) => {
    const configurableParameters = useArrayField({ field: "configurableParameters" });

    return (
        <div className="row">
            {configurableParameters.fields.map(({ field, key }, i) => (
                <div key={key} className="col-md-6">
                    <label>{(configurableParameters.fields[i] as any).initialValue.name}</label>
                    <p className="help-block">{(configurableParameters.fields[i] as any).initialValue.description}</p>
                    <TextBox id={`${field}.parameterValue`} field={`${field}.parameterValue`} type="number" min="1" max="100" required />
                </div>
            ))}
        </div>
    )
}

export const NotificationConfigForm = ({ selectedTemplate, existingSubscription, isEdit, onClose }: NotificationConfigProps) => {
    const [users, setUsers] = React.useState<User[]>();
    const [roles, setRoles] = React.useState<Role[]>();
    const [devices, setDevices] = React.useState<DeviceType[]>();
    const [alertSummaryText, setAlertSummaryText] = React.useState<string>();
    const [submitting, setSubmitting] = React.useState(false);
    const [errorAlert, setErrorAlert] = React.useState<string>(null);
    const client = useWebPortalApiClient();
    const formApi = useFormApi<NotificationConfigFormState>();
    const { study } = useCurrentStudyConfigurationContext();

    React.useEffect(() => {
        const getUsers = async () => {
            const result = await client.client.getUsers();

            setUsers(result.value);

            const rolesResult = await client.client.getRoles();
            setRoles(rolesResult.value);

            const deviceResult = await client.client.getDeviceTypes();
            setDevices(deviceResult.value);
        }

        getUsers();

        return () => {
            client.abortController.abort();
        }
    }, []);

    const onSubmit = async () => {
        setSubmitting(true);
        setErrorAlert(null);
        formApi.validate();

        let state = formApi.getState();

        if ((state.values.users === null || state.values.users.length === 0) &&
            (state.values.roles === null ||state.values.roles.length === 0) &&
            state.values.notifySite === false &&
            state.values.notifyAuthor === false)
            formApi.setFormError("At least one notification option should be selected");

        state = formApi.getState();
        if (state.invalid) {
            setSubmitting(false);
            return setErrorAlert(state.error ?? "Errors exist on the page");
        }

        try {
            if (isEdit) {
                const patchResult = await client.client.patchNotificationSubscription({
                    studyId: study.id,
                    notifyImmediately: false,
                    notifyAuthor: state.values.notifyAuthor,
                    notifySite: state.values.notifySite,
                    usersToNotifyIds: state.values.users?.map(u => u.id) ?? [],
                    rolesToNotifyIds: state.values.roles?.map(r => r.id) ?? []
                }, existingSubscription.id);
            } else {
                const postResult = await client.client.postNotificationSubscription({
                    studyId: study.id,
                    pluginId: selectedTemplate.Id,
                    pluginName: selectedTemplate.Name,
                    notifyImmediately: false,
                    notifyAuthor: state.values.notifyAuthor,
                    notifySite: state.values.notifySite,
                    usersToNotifyIds: state.values.users?.map(u => u.id) ?? [],
                    rolesToNotifyIds: state.values.roles?.map(r => r.id) ?? []
                });
            }
        } catch (err) {
            setErrorAlert(err);
            return;
        }

        onClose();
        setSubmitting(false);
    }

    return (
        <>
            <div className="modal-body">
                <Alert show={errorAlert !== null} category={AlertCategoryEnum.Danger}>
                    {errorAlert}
                </Alert>
                <h2 className="text-primary">Configure Notification</h2>
                <div className="row">
                    
                    <div className="form-group col-md-6 col-sm-6">
                        <label>Individual Users to Notify</label>
                        <MultiSelect
                            id="users"
                            field="users"
                            options={users}
                            labelKey="email"
                            valueKey="id"
                            multi
                        />
                    </div>
                    <div className="form-group col-md-6 col-sm-6">
                        <label>Roles to Notify</label>
                        <MultiSelect
                            id="roles"
                            field="roles"
                            options={roles}
                            labelKey="name"
                            valueKey="id"
                            multi
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="form-group col-md-4 col-sm-4">
                        <Checkbox id="notifyAuthor" label="Notify the Author" field="notifyAuthor" />
                    </div>
                    {/*<div className="form-group col-md-4 col-sm-4">*/}
                    {/*    <Checkbox id="immediateSend" label="Notify immediately on calculation" field="immediateSend" />*/}
                    {/*</div>*/}
                    <div className="form-group col-md-4 col-sm-4">
                        <Checkbox id="notifySite" label="Notify users assigned to site" field="notifySite" />
                    </div>
                </div>
                <CreateNotificationConfigParameters selectedTemplate={selectedTemplate} />
            </div>
            <div className="modal-footer">
                <AsyncActionButton className="btn btn-primary" type="button" onClick={() => onSubmit()} isSubmitting={submitting}>Save</AsyncActionButton>
            </div>
        </>
    );
}

export const NotificationConfig = (props: NotificationConfigProps) => {
    let formInitialState = null;
    if (!props.existingSubscription) {
        formInitialState = {
            users: [],
            roles: [],
            notifySite: false,
            notifyAuthor: false,
            configurableParameters: props.selectedTemplate.ConfigurableParameters.map(st => ({
                name: st.Name,
                description: st.Description,
                parameterValue: 0
            }))
        }
    } else {
        formInitialState = {
            users: props.existingSubscription.usersToNotify,
            roles: props.existingSubscription.rolesToNotify,
            notifySite: props.existingSubscription.notifySite,
            notifyAuthor: props.existingSubscription.notifyAuthor,
            configurableParameters: props.selectedTemplate.ConfigurableParameters.map(st => ({
                name: st.Name,
                description: st.Description,
                parameterValue: 0
            }))
        }
    }

    return (
        <Form<NotificationConfigFormState> initialValues={formInitialState}>
            <NotificationConfigForm {...props} />
        </Form>
    )
}
